import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Box,
  CardActionArea,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  FavoriteBorder,
  Favorite,
  BookmarkBorder,
  // Bookmark,
  Share,
} from '@mui/icons-material';
import { Fact } from '../../types';
import { likeFact } from '../../store/slices/factsSlice';
import { RootState } from '../../store';

interface FactCardProps {
  fact: Fact;
  variant?: 'default' | 'highlighted';
  hideActions?: boolean;
}

const FactCard: React.FC<FactCardProps> = ({
  fact,
  variant = 'default',
  hideActions = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const userId = useSelector((state: RootState) => state.auth.user?.id);

  const [liked, setLiked] = React.useState(fact.likedBy.includes(userId || ''));
  const [likeCount, setLikeCount] = React.useState(fact.likes);

  // 類別對應中文名稱
  const categoryNames: Record<string, string> = {
    nature: '自然與生物',
    human: '人體與心理',
    history: '歷史與文化',
    science: '科學與宇宙',
    tech: '科技與網路',
    food: '食物與日常生活',
    travel: '世界奇觀與旅遊',
    architecture: '建築與城市',
    law: '法律與規則',
    probability: '機率與巧合',
    others: '其他',
  };

  // 獲取類別對應的顏色
  const getCategoryColor = (category: string): string => {
    const customColors: Record<string, string> = {
      nature: '#2e7d32', // 深綠色
      human: '#1976d2', // 藍色
      history: '#9c27b0', // 紫色
      science: '#0288d1', // 淺藍色
      tech: '#0097a7', // 青色
      food: '#d32f2f', // 紅色
      travel: '#ed6c02', // 橙色
      architecture: '#7b1fa2', // 深紫色
      law: '#c2185b', // 洋紅色
      probability: '#1565c0', // 深藍色
      others: '#757575', // 灰色
    };

    return customColors[category] || '#757575';
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    // 樂觀更新 UI
    setLiked(!liked);
    setLikeCount((prevCount) => (liked ? prevCount - 1 : prevCount + 1));

    // 發送 API 請求
    dispatch(likeFact(fact.id));
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    // 實際專案中這裡會處理收藏邏輯
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();

    // 建立分享的URL
    const url = `${window.location.origin}/facts/${fact.id}`;

    // 使用 Web Share API (如果可用)
    if (navigator.share) {
      navigator
        .share({
          title: fact.title,
          text: fact.content.substring(0, 100) + '...',
          url: url,
        })
        .catch((err) => {
          console.error('分享失敗:', err);
          // 如果Web Share API失敗，降級到複製到剪貼簿
          copyToClipboard(url);
        });
    } else {
      // 降級到複製到剪貼簿
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert('連結已複製到剪貼簿！');
      })
      .catch((err) => {
        console.error('無法複製連結:', err);
      });
  };

  const handleCardClick = () => {
    navigate(`/facts/${fact.id}`);
  };

  // 格式化日期
  // const formatDate = (dateString: string) => {
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString('zh-TW', {
  //     year: 'numeric',
  //     month: 'long',
  //     day: 'numeric',
  //   });
  // };

  // 獲取顯示的類別名稱
  const displayCategoryName = categoryNames[fact.category] || fact.category;

  // 獲取類別背景顏色
  const categoryBgColor = getCategoryColor(fact.category);

  return (
    <Card
      className={`${
        variant === 'highlighted' ? 'border-2 border-primary-main' : ''
      } h-full flex flex-col`}
      elevation={variant === 'highlighted' ? 3 : 1}
      sx={{
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
    >
      <CardActionArea onClick={handleCardClick} sx={{ flexGrow: 1 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={1}
          >
            <Typography variant="h6" component="h2" className="font-bold">
              {fact.title}
            </Typography>
            <Chip
              label={displayCategoryName}
              size="small"
              sx={{
                ml: 1,
                bgcolor: categoryBgColor,
                color: 'white',
                fontWeight: 500,
              }}
            />
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            className="mb-4"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {fact.content}
          </Typography>

          {/* <Typography
            variant="caption"
            color="text.secondary"
            className="block mt-2"
          >
            {formatDate(fact.date)}
          </Typography> */}
        </CardContent>
      </CardActionArea>

      {!hideActions && (
        <>
          <Divider />
          <CardActions disableSpacing>
            <Tooltip title={liked ? '取消讚' : '讚'}>
              <IconButton
                aria-label="like"
                color={liked ? 'secondary' : 'default'}
                onClick={handleLike}
                size="small"
              >
                {liked ? <Favorite /> : <FavoriteBorder />}
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {likeCount}
            </Typography>

            <Tooltip title="收藏">
              <IconButton aria-label="save" onClick={handleSave} size="small">
                <BookmarkBorder />
              </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title="分享">
              <IconButton aria-label="share" onClick={handleShare} size="small">
                <Share />
              </IconButton>
            </Tooltip>
          </CardActions>
        </>
      )}
    </Card>
  );
};

export default FactCard;
